import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from "./ui/card";
import { Button } from "./ui/button";
import { Alert, AlertDescription } from "./ui/alert";
import { Loader2, Sparkles, RefreshCw, Copy, Check } from "lucide-react";
import { Badge } from "./ui/badge";
import { supabase } from "../supabaseClient";

const AISummaryCard = ({
  userResponses,
  situationTitle,
  situationDescription = "",
  situationId,
  groupId, // <-- pridaj nový prop
}) => {
  const [summary, setSummary] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  // Load existing summary from database on component mount
  useEffect(() => {
    if (situationId && groupId) {
      loadExistingSummary();
    }
  }, [situationId, groupId]);

  const loadExistingSummary = async () => {
    try {
      const { data, error } = await supabase
        .from("ai_summaries")
        .select("ai_summary")
        .eq("situation_id", situationId)
        .eq("group_id", groupId)
        .single();

      if (error && error.code !== "PGRST116") {
        console.error("Error loading existing summary:", error);
        return;
      }

      if (data && data.ai_summary) {
        setSummary(data.ai_summary);
      } else {
        setSummary("");
      }
    } catch (err) {
      console.error("Error loading existing summary:", err);
    }
  };

  const generateSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      // Call our dedicated summary API
      const response = await fetch("/api/generate-summary", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          situationId,
          groupId, // <-- posielaj groupId
          situationTitle,
          situationDescription,
          responses: userResponses,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Nepodarilo sa vygenerovať súhrn");
      }

      const data = await response.json();
      setSummary(data.summary);

      // Show warning if summary was generated but not saved
      if (data.warning) {
        console.warn(data.warning);
      }
    } catch (err) {
      console.error("Error generating summary:", err);
      setError(err.message || "Nepodarilo sa vygenerovať súhrn");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(summary);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy summary:", err);
    }
  };

  return (
    <Card className="mt-8">
      <CardHeader className="flex flex-row justify-between items-start pb-2 space-y-0">
        <div>
          <CardTitle className="flex items-center">
            <Sparkles className="mr-2 w-5 h-5 text-purple-500" />
            AI Súhrn odpovedí
          </CardTitle>
          <CardDescription>
            Automaticky generovaný súhrn a analýza odpovedí
          </CardDescription>
        </div>

        <Badge
          variant="outline"
          className="text-purple-700 bg-purple-50 border-purple-200"
        >
          {userResponses.length} odpovedí
        </Badge>
      </CardHeader>

      <CardContent>
        {!summary && !loading && (
          <div className="flex flex-col justify-center items-center py-10 rounded-lg border border-dashed bg-slate-50">
            <Sparkles className="mb-3 w-10 h-10 text-purple-200" />
            <p className="px-4 mb-4 text-center text-slate-500">
              {userResponses.length > 0
                ? "Kliknite pre vygenerovanie súhrnu a analýzy odpovedí pomocou AI"
                : "Najskôr je potrebné mať odpovede používateľov"}
            </p>
            <Button
              onClick={generateSummary}
              disabled={userResponses.length === 0}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Sparkles className="mr-2 w-4 h-4" />
              Generovať AI súhrn
            </Button>
          </div>
        )}

        {loading && (
          <div className="flex flex-col justify-center items-center py-10">
            <Loader2 className="mb-4 w-8 h-8 text-purple-500 animate-spin" />
            <p className="text-slate-600">
              Generujem súhrn a analýzu odpovedí...
            </p>
            <p className="mt-2 text-xs text-slate-400">
              Tento proces môže trvať niekoľko sekúnd
            </p>
          </div>
        )}

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {summary && !loading && (
          <div className="p-6 whitespace-pre-line bg-purple-50 rounded-lg border border-purple-100 text-slate-800">
            {summary}
          </div>
        )}
      </CardContent>

      {summary && !loading && (
        <CardFooter className="flex justify-end pt-2 space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={generateSummary}
            className="flex items-center"
          >
            <RefreshCw className="mr-1.5 w-4 h-4" />
            <span>Aktualizovať</span>
          </Button>

          <Button
            variant="secondary"
            size="sm"
            onClick={handleCopy}
            className={copied ? "text-green-600 bg-green-50" : ""}
          >
            {copied ? (
              <>
                <Check className="mr-1.5 w-4 h-4" />
                <span>Skopírované</span>
              </>
            ) : (
              <>
                <Copy className="mr-1.5 w-4 h-4" />
                <span>Kopírovať</span>
              </>
            )}
          </Button>
        </CardFooter>
      )}
    </Card>
  );
};

export default AISummaryCard;
